import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ReactComponent as ChevronIcon } from "../../assets/images/icons/ic-chevron.svg"
import UserPlaceholder from "../../assets/images/placeholders/user-placeholder.png"
import { SurveyType } from "../../common/constants"
import { Each } from "../../common/Each"
import Pagination from "../Pagination"
import TextInput from "../TextInput"
import styles from "./SurveyQuestion.module.css"
import ReactMarkdown from "react-markdown"

const SurveyQuestion = ({ question, surveyId }) => {

    const [expandedId, setExpandedId] = useState(null)
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(0)
    const navigate = useNavigate()

    const per = 8

    const parseSingleChoice = (question) => {
        let res = []
        let nAnswers = 0
        for (let i = 0; i < question.options.length; i++) {
            const a = question.answers?.filter(a => a.id === i)
            if (a) {
                nAnswers += a.length
                res.push({ ...question.options[i], answers: a })
            } else {
                res.push({ ...question.options[i], answers: [] })
            }
        }
        res = res.map(o => ({ ...o, percentage: nAnswers > 0 ? Math.round(o.answers.length / nAnswers * 100 * 100) / 100 : 0 }))
        const maxPercentage = Math.max(...res.map(o => o.percentage))
        res = res.map(o => ({ ...o, isMax: o.percentage === maxPercentage }))
        return res
    }

    const parseMultipleChoice = (question) => {
        let res = []
        let nAnswers = 0
        for (let i = 0; i < question.options.length; i++) {
            const a = []
            if (question.answers) {
                for (const userAnswer of question.answers) {
                    const { answers, ...user } = userAnswer
                    for (const answer of answers) {
                        if (answer.id === i) {
                            a.push({ ...answer, ...user })
                        }
                    }
                }
                nAnswers += a.length
                res.push({ ...question.options[i], answers: a })
            } else {
                res.push({ ...question.options[i], answers: [] })
            }
        }
        res = res.map(o => ({ ...o, percentage: nAnswers ? Math.round(o.answers.length / nAnswers * 100 * 100) / 100 : 0 }))
        const maxPercentage = Math.max(...res.map(o => o.percentage))
        res = res.map(o => ({ ...o, isMax: o.percentage === maxPercentage }))
        return res
    }

    const parseNumeric = (question) => {
        let res = []
        let nAnswers = 0
        const { min, max, step } = question
        let optionIndex = 0
        for (let value = min; value <= max; value += step, optionIndex++) {
            const currentIndex = optionIndex
            const answersForOption = question.answers
                ? question.answers.filter((answer) => answer.id === currentIndex)
                : []
            nAnswers += answersForOption.length
            res.push({ value, answers: answersForOption })
        }
        res = res.map(o => ({ ...o, percentage: nAnswers ? Math.round(o.answers.length / nAnswers * 100 * 100) / 100 : 0 }))
        const maxPercentage = Math.max(...res.map(o => o.percentage))
        res = res.map(o => ({ ...o, isMax: o.percentage === maxPercentage }))
        return res
    }

    const optionsWithAnswers = useMemo(() => {
        if (!question) {
            return []
        }
        switch (question.type) {
            case SurveyType.SingleChoice: return parseSingleChoice(question);
            case SurveyType.MultipleChoice: return parseMultipleChoice(question);
            case SurveyType.Numeric: return parseNumeric(question);
            case SurveyType.Open: return [{ value: 'Risposte aperte', answers: question.answers || [] }];
            default: break;
        }
    }, [question])

    const selectedAnswers = optionsWithAnswers?.[expandedId]?.answers || []
    const filteredStudents = selectedAnswers.filter(user =>
        `${user.name || ''} ${user.surname || ''} ${user.answerValue || ''}`.toLowerCase().includes(search.trim().toLowerCase())
    )

    return (
        <div className={styles.container}>
            <div className={styles.title}><ReactMarkdown>{question.text}</ReactMarkdown></div>
            <Each
                of={optionsWithAnswers}
                render={(option, index) => (
                    <div className={styles.answerWrapper}>
                        <div className={styles.answerContainer}>
                            <div className={styles.answerSection}>
                                <div className={styles.answer}>
                                <ReactMarkdown>{`${option.value}`}</ReactMarkdown>
                                </div>
                            </div>
                            {question.type === SurveyType.Open ? <div className={styles.openCount}>{option.answers.length} {option.answers.length === 1 ? 'risposta' : 'risposte'}</div> : <div className={styles.stats}>
                                <div className={styles.percentage}>{option.percentage} %</div>
                                <div className={styles.barContainer}>
                                    <div className={styles.bar}
                                        style={{
                                            width: `${option.percentage}%`,
                                            backgroundColor: option.isMax ? "var(--primary)" : "rgba(var(--text-color-rgb), 32%)"
                                        }}
                                    />
                                </div>
                            </div>}
                            <button
                                aria-label={expandedId === index ? "Nascondi risposte" : "Mostra i partecipanti che hanno risposto"}
                                aria-expanded={expandedId === index}
                                disabled={option.answers.length === 0}
                                className={expandedId === index ? `${styles.chevron} ${styles.expanded}` : styles.chevron}
                                onClick={() => {
                                    if (expandedId === index) {
                                        setExpandedId(null)
                                    } else {
                                        setExpandedId(index)
                                        setSearch("")
                                        setPage(0)
                                    }
                                }}
                            >
                                <ChevronIcon style={{
                                    transform: `rotate(${expandedId === index ? "0deg" : "180deg"})`,
                                }} />
                            </button>
                        </div>
                        {expandedId === index && <section className={styles.students} aria-label="Risposte dei partecipanti">
                            <div className={styles.studentsToolbar}>
                                <span className={styles.responseCount} role="status">{filteredStudents.length} {filteredStudents.length === 1 ? 'risposta' : 'risposte'}</span>
                                <label className={styles.search}><span className={styles.srOnly}>Cerca partecipante o risposta</span><TextInput appearance="modern" type="search" placeholder="Cerca partecipante o risposta…" value={search} onKeyUp={value => { setSearch(value); setPage(0) }} /></label>
                            </div>
                            <div className={styles.studentList}>
                                {filteredStudents.slice(page * per, (page + 1) * per).map((user, userIndex) => <button key={`${user.userId}-${userIndex}`} type="button" className={styles.student} onClick={() => navigate(`/surveys/${surveyId}/students/${user.userId}`)}>
                                    <img src={user.picture || UserPlaceholder} alt="" onError={({ currentTarget }) => { currentTarget.onerror = null; currentTarget.src = UserPlaceholder }} />
                                    <span className={styles.studentContent}>
                                        <span className={styles.studentName}>{user.name} {user.surname}</span>
                                        {user.answerValue && <span className={styles.studentResponse}>{user.answerValue}</span>}
                                    </span>
                                    <span className={styles.detailLink}>Vedi risposte <span aria-hidden="true">↗</span></span>
                                </button>)}
                                {!filteredStudents.length && <p className={styles.empty}>Nessuna risposta corrisponde alla ricerca.</p>}
                            </div>
                            {filteredStudents.length > per && <div className={styles.pagination}><Pagination page={page} per={per} total={filteredStudents.length} onChange={setPage} /></div>}
                        </section>}

                    </div>
                )}
            />
        </div>
    )

}

export default SurveyQuestion